import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../dashboard-landing/Navbar';
import Footer from '../dashboard-landing/Footer';
import { Mail, CheckCircle, AlertCircle, Loader2, XCircle } from 'lucide-react';

/**
 * VerifyEmailChange Component
 * Verifies email change requests using token from URL
 */
const VerifyEmailChange = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
        const verifyEmailChange = async () => {
            if (!token) {
                setStatus('error');
                setMessage('Invalid or missing verification token. Please request a new email change.');
                return;
            }

            try {
                const response = await api.post(`/api/email-change/verify`, {
                    token: token,
                });

                if (response.data.success) {
                    setStatus('success');
                    setMessage(response.data.message || 'Email address updated successfully!');
                    
                    // Clear tokens and redirect to login after 3 seconds
                    setTimeout(() => {
                        localStorage.removeItem('token');
                        localStorage.removeItem('userRole');
                        localStorage.removeItem('userData');
                        localStorage.removeItem('mfaVerificationToken');
                        localStorage.removeItem('tacDismissedVersion');
                        navigate('/login');
                    }, 3000);
                } else {
                    setStatus('error');
                    setMessage(response.data.message || 'Failed to verify email change.');
                }
            } catch (err) {
                console.error('Email verification error:', err);
                setStatus('error');
                // Show specific error message from backend
                const errorMessage = err.response?.data?.message || 
                    'Failed to verify email change. The token may have expired or the email address is already in use. Please request a new email change.';
                
                setMessage(errorMessage);
            }
        };

        verifyEmailChange();
    }, [token, navigate]);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            <main className="flex-grow flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6">
                    {/* Icon and Title */}
                    <div className="text-center">
                        {status === 'verifying' && (
                            <Loader2 className="mx-auto h-16 w-16 text-blue-600 animate-spin mb-4" />
                        )}
                        {status === 'success' && (
                            <CheckCircle className="mx-auto h-16 w-16 text-green-600 mb-4" />
                        )}
                        {status === 'error' && (
                            <XCircle className="mx-auto h-16 w-16 text-red-600 mb-4" />
                        )}

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {status === 'verifying' && 'Verifying Email Change...'}
                            {status === 'success' && 'Email Changed Successfully!'}
                            {status === 'error' && 'Verification Failed'}
                        </h2>
                    </div>

                    {/* Message */}
                    {message && (
                        <div 
                            className={`p-4 rounded-lg ${
                                status === 'success' 
                                    ? 'bg-green-100 border-l-4 border-green-500 text-green-800' 
                                    : status === 'error'
                                    ? 'bg-red-100 border-l-4 border-red-500 text-red-800'
                                    : 'bg-blue-100 border-l-4 border-blue-500 text-blue-800'
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                {status === 'success' && <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />}
                                {status === 'error' && <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />}
                                {status === 'verifying' && <Loader2 size={20} className="flex-shrink-0 mt-0.5 animate-spin" />}
                                <p className="text-sm">{message}</p>
                            </div>
                        </div>
                    )}

                    {/* Additional Info */}
                    {status === 'success' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800 mb-2">
                                <Mail size={16} className="inline mr-1" />
                                <strong>Next Steps:</strong>
                            </p>
                            <ul className="text-sm text-blue-700 space-y-1 ml-5 list-disc">
                                <li>You will be automatically logged out</li>
                                <li>Log in with your new email address</li>
                                <li><strong>Set up MFA again for security</strong></li>
                                <li>Your other account settings remain unchanged</li>
                            </ul>
                            <p className="text-xs text-blue-600 mt-3">
                                Redirecting to login in 3 seconds...
                            </p>
                        </div>
                    )}

                    {/* Error Actions */}
                    {status === 'error' && (
                        <div className="space-y-3">
                            <Link
                                to="/login"
                                className="block w-full text-center bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold transition-colors"
                            >
                                Go to Login
                            </Link>
                            <Link
                                to="/"
                                className="block w-full text-center bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                            >
                                Go to Home
                            </Link>
                        </div>
                    )}

                    {/* Success Action */}
                    {status === 'success' && (
                        <Link
                            to="/login"
                            className="block w-full text-center bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold transition-colors"
                        >
                            Go to Login Now
                        </Link>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default VerifyEmailChange;
